import { z } from 'zod';
import type { Contact } from './contact';

// Types pour les relations (à définir plus tard si nécessaire)
export interface Product {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
}

export interface Vehicle {
  id: string;
  registration: string;
}

export interface Association {
  id: string;
  name: string;
}

export interface Discount {
  id: string;
  name: string;
  amount: number;
}

export interface User {
  id: string;
  email: string;
  full_name?: string | null;
}

// =====================================================
// ENUMS
// =====================================================

export const CREW_STATUS = {
  BPI_PAYMENT_PENDING: 'bpi_payment_pending',
  PRE_REGISTERED: 'pre_registered',
  // Cycle BED (migration 00232, cahier L18-23) : le participant a ouvert son
  // bulletin par son lien, puis l'a soumis en laissant des infos ou du solde.
  BED_DOWNLOADED: 'bed_downloaded',
  // Valeur historique de l'enum DB (00001_schema, position 4) : l'équipage a
  // atteint l'inscription définitive. Absente du mapping front jusqu'ici →
  // fiche équipage en crash sur le badge statut.
  REGISTERED: 'registered',
  BED_INCOMPLETE: 'bed_incomplete',
  BED_INCOMPLETE_PAYMENT: 'bed_incomplete_payment',
  BED_COMPLETE: 'bed_complete',
  CANCELLATION_COMPLIANT: 'cancellation_compliant',
  CANCELLATION_NON_COMPLIANT: 'cancellation_non_compliant',
  POSTPONEMENT_COMPLIANT: 'postponement_compliant',
  POSTPONEMENT_NON_COMPLIANT: 'postponement_non_compliant',
  COMPLETED: 'completed',
  ARCHIVED: 'archived',
} as const;

export type CrewStatus = (typeof CREW_STATUS)[keyof typeof CREW_STATUS];

export const CREW_PROBABILITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export type CrewProbability = (typeof CREW_PROBABILITY)[keyof typeof CREW_PROBABILITY];

export const CREW_FINANCING_TYPE = {
  SELF_FINANCING: 'self_financing',
  SCHOOL: 'school',
  ASSOCIATION: 'association',
  SPONSORSHIP: 'sponsorship',
  MIXED: 'mixed',
  OTHER: 'other',
} as const;

export type CrewFinancingType = (typeof CREW_FINANCING_TYPE)[keyof typeof CREW_FINANCING_TYPE];

// Rôles des membres d'équipage (pilote/copilote)
export const CREW_MEMBER_ROLE = {
  PILOT: 'pilot',
  COPILOT: 'copilot',
} as const;

export type CrewMemberRole = (typeof CREW_MEMBER_ROLE)[keyof typeof CREW_MEMBER_ROLE];

// =====================================================
// LABELS
// =====================================================

export const CREW_STATUS_LABELS: Record<CrewStatus, string> = {
  [CREW_STATUS.BPI_PAYMENT_PENDING]: 'BPI en attente',
  [CREW_STATUS.PRE_REGISTERED]: 'Pré-inscrit',
  [CREW_STATUS.BED_DOWNLOADED]: 'BED téléchargé',
  [CREW_STATUS.REGISTERED]: 'Inscrit',
  [CREW_STATUS.BED_INCOMPLETE]: 'BED incomplet — infos',
  [CREW_STATUS.BED_INCOMPLETE_PAYMENT]: 'BED incomplet — paiement',
  [CREW_STATUS.BED_COMPLETE]: 'BED complet',
  [CREW_STATUS.CANCELLATION_COMPLIANT]: 'Annulation conforme',
  [CREW_STATUS.CANCELLATION_NON_COMPLIANT]: 'Annulation non conforme',
  [CREW_STATUS.POSTPONEMENT_COMPLIANT]: 'Report conforme',
  [CREW_STATUS.POSTPONEMENT_NON_COMPLIANT]: 'Report non conforme',
  [CREW_STATUS.COMPLETED]: 'Inscrit définitif',
  [CREW_STATUS.ARCHIVED]: 'Archivé',
};

export const CREW_PROBABILITY_LABELS: Record<CrewProbability, string> = {
  [CREW_PROBABILITY.LOW]: 'Faible',
  [CREW_PROBABILITY.MEDIUM]: 'Moyenne',
  [CREW_PROBABILITY.HIGH]: 'Forte',
};

export const CREW_FINANCING_TYPE_LABELS: Record<CrewFinancingType, string> = {
  [CREW_FINANCING_TYPE.SELF_FINANCING]: 'Autofinancement',
  [CREW_FINANCING_TYPE.SCHOOL]: 'École',
  [CREW_FINANCING_TYPE.ASSOCIATION]: 'Association',
  [CREW_FINANCING_TYPE.SPONSORSHIP]: 'Sponsoring',
  [CREW_FINANCING_TYPE.MIXED]: 'Mixte',
  [CREW_FINANCING_TYPE.OTHER]: 'Autre',
};

export const CREW_MEMBER_ROLE_LABELS: Record<CrewMemberRole, string> = {
  [CREW_MEMBER_ROLE.PILOT]: 'Pilote',
  [CREW_MEMBER_ROLE.COPILOT]: 'Copilote',
};

// =====================================================
// COLORS
// =====================================================

export const CREW_STATUS_COLORS: Record<CrewStatus, { bg: string; text: string; border: string }> =
  {
    [CREW_STATUS.BPI_PAYMENT_PENDING]: {
      bg: 'bg-slate-50',
      text: 'text-slate-700',
      border: 'border-slate-200',
    },
    [CREW_STATUS.PRE_REGISTERED]: {
      bg: 'bg-yellow-50',
      text: 'text-yellow-700',
      border: 'border-yellow-200',
    },
    [CREW_STATUS.BED_DOWNLOADED]: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
    },
    // Aligné sur CONTACT_STATUS_COLORS.registered (statuts synchronisés).
    [CREW_STATUS.REGISTERED]: {
      bg: 'bg-teal-50',
      text: 'text-teal-700',
      border: 'border-teal-200',
    },
    [CREW_STATUS.BED_INCOMPLETE]: {
      bg: 'bg-orange-50',
      text: 'text-orange-700',
      border: 'border-orange-200',
    },
    [CREW_STATUS.BED_INCOMPLETE_PAYMENT]: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      border: 'border-orange-300',
    },
    [CREW_STATUS.BED_COMPLETE]: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
    },
    [CREW_STATUS.CANCELLATION_COMPLIANT]: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
    },
    [CREW_STATUS.CANCELLATION_NON_COMPLIANT]: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-300',
    },
    [CREW_STATUS.POSTPONEMENT_COMPLIANT]: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
    },
    [CREW_STATUS.POSTPONEMENT_NON_COMPLIANT]: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-300',
    },
    [CREW_STATUS.COMPLETED]: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-300',
    },
    [CREW_STATUS.ARCHIVED]: {
      bg: 'bg-stone-100',
      text: 'text-stone-600',
      border: 'border-stone-300',
    },
  };

// =====================================================
// INTERFACES
// =====================================================

export interface Crew {
  id: string;
  name: string | null;
  crew_number: string | null;
  status: CrewStatus;
  probability: CrewProbability;
  product_id: string | null;
  vehicle_id: string | null;
  association_id: string | null;
  discount_id: string | null;
  assigned_to: string | null;
  financing_type: CrewFinancingType;
  budget: number | null;
  report: string | null;
  has_derogation: boolean;
  derogation_pending: boolean;
  organizes_pvd: boolean;
  // V2 R4 Phase B : déclaration "possède un véhicule" indépendante de vehicle_id
  has_vehicle: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Relations (optionnelles, chargées selon le besoin)
  product?: Product | null;
  vehicle?: Vehicle | null;
  association?: Association | null;
  discount?: Discount | null;
  assigned_user?: { id: string; email: string; full_name: string | null } | null;
  members?: ContactWithRole[];
  // Nombre d'appels total de tous les membres de l'équipage (Sprint 4.3)
  call_count?: number;
  // Item #4 retours client : code postal et ville du pilote, alimentés par crews_filter_view
  pilot_postal_code?: string | null;
  pilot_city?: string | null;
  // Bug 2 fix-tests-empiriques (R7) : nom complet du pilote (concat first+last) via crews_filter_view
  pilot_full_name?: string | null;
  // R10 : code BPI résolu via crew_members → form_submissions (vue crews_filter_view)
  bpi_code?: string | null;
  // Synthèse BED (migration 00232, RPC get_crews_bed_summary) — renseignée
  // uniquement sur le chemin d'export CSV, `null` partout ailleurs.
  bed_missing_count?: number | null;
  bed_missing_labels?: string | null;
  bed_remaining_cents?: number | null;
}

export interface CrewMember {
  id: string;
  crew_id: string;
  contact_id: string;
  role: CrewMemberRole | null;
  created_at: string;
  updated_at: string;
  // Relations
  contact?: Contact;
}

/**
 * Contact avec son rôle dans l'équipage
 * Utilisé pour l'affichage dans CrewMembersCard
 */
export interface ContactWithRole extends Contact {
  role?: CrewMemberRole | null;
  derogation_validated?: boolean;
}

// =====================================================
// ZOD SCHEMAS
// =====================================================

export const crewStatusSchema = z.enum([
  CREW_STATUS.BPI_PAYMENT_PENDING,
  CREW_STATUS.PRE_REGISTERED,
  CREW_STATUS.BED_DOWNLOADED,
  CREW_STATUS.REGISTERED,
  CREW_STATUS.BED_INCOMPLETE,
  CREW_STATUS.BED_INCOMPLETE_PAYMENT,
  CREW_STATUS.BED_COMPLETE,
  CREW_STATUS.CANCELLATION_COMPLIANT,
  CREW_STATUS.CANCELLATION_NON_COMPLIANT,
  CREW_STATUS.POSTPONEMENT_COMPLIANT,
  CREW_STATUS.POSTPONEMENT_NON_COMPLIANT,
  CREW_STATUS.COMPLETED,
  CREW_STATUS.ARCHIVED,
]);

export const crewProbabilitySchema = z.enum([
  CREW_PROBABILITY.LOW,
  CREW_PROBABILITY.MEDIUM,
  CREW_PROBABILITY.HIGH,
]);

export const crewFinancingTypeSchema = z.enum([
  CREW_FINANCING_TYPE.SELF_FINANCING,
  CREW_FINANCING_TYPE.SCHOOL,
  CREW_FINANCING_TYPE.ASSOCIATION,
  CREW_FINANCING_TYPE.SPONSORSHIP,
  CREW_FINANCING_TYPE.MIXED,
  CREW_FINANCING_TYPE.OTHER,
]);

export const crewSchema = z.object({
  id: z.string().uuid(),
  name: z.string().nullable(),
  crew_number: z.string().nullable(),
  status: crewStatusSchema,
  probability: crewProbabilitySchema,
  product_id: z.string().uuid().nullable(),
  vehicle_id: z.string().uuid().nullable(),
  association_id: z.string().uuid().nullable(),
  discount_id: z.string().uuid().nullable(),
  assigned_to: z.string().uuid().nullable(),
  financing_type: crewFinancingTypeSchema,
  budget: z.number().nullable(),
  report: z.string().nullable(),
  has_derogation: z.boolean(),
  derogation_pending: z.boolean(),
  organizes_pvd: z.boolean(),
  // V2 R4 Phase B
  has_vehicle: z.boolean(),
  notes: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** Chiffres significatifs d'une saisie ("EQ0013" -> "13", "ABC" -> ""). */
function crewNumberDigits(raw: string): string {
  return raw.replace(/\D/g, '').replace(/^0+/, '');
}

export const crewNumberSchema = z
  .string({ message: "Le numéro d'équipage est requis" })
  .min(1, "Le numéro d'équipage est requis")
  // Normalisation sur la chaîne de chiffres, jamais via parseInt : au-delà de
  // 1e21 JS bascule en notation scientifique ("1e+21") et le numéro deviendrait
  // silencieusement 0121. La RPC de création applique ensuite le padding propre
  // au produit (4L ou trek).
  .superRefine((val, ctx) => {
    if (crewNumberDigits(val).length > 9) {
      ctx.addIssue({
        code: 'custom',
        message: "Le numéro d'équipage est trop long (9 chiffres maximum)",
      });
    }
  })
  .transform((val) => {
    const digits = crewNumberDigits(val);
    if (!digits) return val;
    return digits.padStart(4, '0');
  });

// Pipe plutot qu'union : les messages de crewNumberSchema (ex. numero trop long)
// remontent tels quels, la ou une union les aplatit en « Invalid input ».
const optionalCrewNumberSchema = z
  .string()
  .transform((val) => (val.trim() === '' ? undefined : val))
  .pipe(crewNumberSchema.optional())
  .optional();

// Produit obligatoire (demande Floran 03/09) : sans produit, ni compteur ni
// réservations, l'assistance numéro est inopérante. Contrainte au formulaire
// uniquement : la FK crews.product_id est ON DELETE SET NULL, un NOT NULL en
// base bloquerait la suppression d'un produit.
const requiredProductIdSchema = z
  .string({ message: 'Le produit est requis' })
  .min(1, 'Le produit est requis')
  .uuid('Le produit sélectionné est invalide');

export const createCrewSchema = z.object({
  name: z.string().optional(),
  crew_number: optionalCrewNumberSchema,
  // Le statut est maintenant sélectionnable manuellement
  status: crewStatusSchema.optional(),
  probability: crewProbabilitySchema,
  product_id: requiredProductIdSchema,
  vehicle_id: z
    .union([z.string().uuid('Le véhicule sélectionné est invalide'), z.literal(''), z.undefined()])
    .optional(),
  association_id: z
    .union([
      z.string().uuid("L'association sélectionnée est invalide"),
      z.literal(''),
      z.undefined(),
    ])
    .optional(),
  discount_id: z
    .union([
      z.string().uuid('La réduction sélectionnée est invalide'),
      z.literal(''),
      z.undefined(),
    ])
    .optional(),
  assigned_to: z
    .union([z.string().uuid("L'utilisateur assigné est invalide"), z.literal(''), z.undefined()])
    .optional(),
  financing_type: crewFinancingTypeSchema.optional(),
  budget: z
    .union([z.number().positive('Le budget doit être positif'), z.nan(), z.undefined()])
    .optional(),
  report: z.string().optional(),
  has_derogation: z.boolean().optional(),
  derogation_pending: z.boolean().optional(),
  organizes_pvd: z.boolean().optional(),
  // V2 R4 Phase B
  has_vehicle: z.boolean().optional(),
  notes: z.string().optional(),
  // Code BPI optionnel pour associer automatiquement les contacts (format: BPI-XXXXXX)
  bpi_code: z.string().optional(),
});

export const updateCrewSchema = createCrewSchema.partial().extend({
  // L'édition d'un équipage numéroté conserve le contrôle historique : une
  // chaîne vide ne nullifie pas silencieusement le numéro.
  crew_number: crewNumberSchema.optional(),
  // Déjà le cas via partial(), répété pour rendre la règle lisible : absent =
  // inchangé, mais on ne dé-rattache pas un équipage de son produit par une
  // chaîne vide.
  product_id: requiredProductIdSchema.optional(),
});

// Fiche d'un équipage SANS numéro (pré-inscrit créé à la main) : un champ vide
// veut dire « toujours pas de numéro », pas une erreur. Sinon la fiche entière
// (notes, budget…) devient non enregistrable tant qu'un numéro n'est pas saisi.
export const updateUnnumberedCrewSchema = updateCrewSchema.extend({
  crew_number: optionalCrewNumberSchema,
});

export type CreateCrewInput = z.infer<typeof createCrewSchema>;
export type UpdateCrewInput = z.infer<typeof updateCrewSchema>;
